import { CalendarDaysIcon, XIcon } from 'lucide-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { listEvents, type EventState, type EventSummary } from '@/lib/events';
import { Checkbox } from '@/components/ui/checkbox';
import { eventStageTone } from '@/lib/event-status';
import { formatEventDate, formatEventYear } from '@/lib/event-time';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';

/**
 * Sortable columns → the server-side field each orders by. Lead is absent on
 * purpose: it's a populated ref Mongo can't sort without a lookup.
 */
const EVENT_SORT_FIELDS = {
  date: 'eventDate',
  event: 'contractNumber',
  participants: 'participants',
  location: 'startVenue.city',
  stage: 'stageName',
} as const;
const EVENT_SORT_KEYS = Object.values(EVENT_SORT_FIELDS);

/** Debounce for the search box, so typing does not hammer the API. */

/**
 * Salesforce stage names mapped to a tone the eye can triage by. Unknown
 * stages render neutral rather than crashing on a new pipeline stage.
 */
/**
 * Renders a date as the two-line block the list scans by.
 *
 * @param iso - The event date.
 * @returns Month/day over year, or a dash for undated events.
 */
function EventDate({ event }: { readonly event: EventSummary }): React.ReactElement {
  const day = formatEventDate(event, 'short');
  if (day === undefined) return <span className="text-portal-muted">—</span>;
  // Line metrics deliberately mirror the Event cell (small bold line over a
  // micro muted line) so the two columns baseline-align row by row.
  return (
    <span className="block">
      <b className="block text-portal-small">{day}</b>
      <span className="block text-portal-micro text-portal-muted">{formatEventYear(event)}</span>
    </span>
  );
}

/**
 * The events workspace: every BCE event, filterable by time and searchable by
 * program, client or contract number.
 *
 * Designed around how the team actually looks for an event — "the KPMG one in
 * Chicago next week" — so the row leads with the date and the program+client
 * pair, not the contract number. State and search live in the URL, so a
 * filtered view can be sent to a colleague.
 *
 * @returns The events list.
 */
export function EventsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const state = (params.get('state') as EventState | null) ?? 'upcoming';
  const showAwaiting = params.get('awaiting') === '1';
  const family = params.get('family') ?? '';
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();

  const { sort, onSort } = useTableSort(EVENT_SORT_KEYS);
  const events = useQuery({
    queryKey: ['events', state, debounced, sort, showAwaiting, page, family],
    queryFn: ({ signal }) =>
      listEvents(
        {
          state,
          search: debounced,
          page,
          ...(sort ? { sort } : {}),
          ...(showAwaiting ? { awaitingContract: 'include' as const } : {}),
          ...(family ? { family } : {}),
        },
        signal,
      ),
    // Keep the previous rows while a new filter loads: a list that blanks on
    // every keystroke reads as flicker, not feedback.
    placeholderData: keepPreviousData,
  });

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="OPERATIONS"
        title="Events"
        subtitle="Every BCE event — who it's for, where it happens and who is delivering it."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          data-guide="events-search"
          label="Search events"
          placeholder="Program, client, contact or contract #"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <div data-guide="events-state"><SegmentedControl
          label="Which events to show"
          options={[
            { key: 'upcoming', label: 'Upcoming' },
            { key: 'past', label: 'Past' },
            { key: 'all', label: 'All' },
          ]}
          value={state}
          onChange={(next) =>
            setParams((previous) => {
              const nextParams = new URLSearchParams(previous);
              nextParams.set('state', next);
              nextParams.delete('page');
              return nextParams;
            })
          }
        /></div>
        <label data-guide="events-awaiting" className="flex items-center gap-2 text-portal-small text-portal-ink">
          <Checkbox
            checked={showAwaiting}
            onCheckedChange={(checked) =>
              setParams((previous) => {
                const nextParams = new URLSearchParams(previous);
                if (checked === true) nextParams.set('awaiting', '1');
                else nextParams.delete('awaiting');
                nextParams.delete('page');
                return nextParams;
              })
            }
          />
          Include awaiting-contract
        </label>
        {family ? (
          <button
            type="button"
            onClick={() =>
              setParams((previous) => {
                const nextParams = new URLSearchParams(previous);
                nextParams.delete('family');
                nextParams.delete('page');
                return nextParams;
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-brand bg-brand-tint px-2.5 py-0.5 text-portal-micro font-semibold text-brand hover:bg-white"
            aria-label={`Stop filtering to contract family ${family}`}
          >
            Contract {family} <XIcon aria-hidden className="size-3" />
          </button>
        ) : null}
        {events.data === undefined ? null : (
          <span className="text-portal-micro text-portal-muted">
            {events.data.total} {events.data.total === 1 ? 'event' : 'events'}
          </span>
        )}
      </div>

      {events.isError ? (
        <ErrorNotice title="Events unavailable" message={errorMessage(events.error)} />
      ) : events.isPending ? (
        <SkeletonTable label="Loading events…" />
      ) : events.data.events.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message="No events match that search." />
          ) : (
            <EmptyState
              icon={<CalendarDaysIcon aria-hidden className="size-6" />}
              title={state === 'upcoming' ? 'Nothing on the calendar yet' : 'No events here'}
              message="Events sync in from Salesforce and appear here as they are booked."
            />
          )}
        </Panel>
      ) : (
        <div data-guide="events-table"><DataTable
          headers={[
            { label: 'Date', sortKey: EVENT_SORT_FIELDS.date },
            { label: 'Event', sortKey: EVENT_SORT_FIELDS.event },
            { label: 'Participants', sortKey: EVENT_SORT_FIELDS.participants },
            { label: 'Location', sortKey: EVENT_SORT_FIELDS.location },
            'Lead',
            { label: 'Stage', sortKey: EVENT_SORT_FIELDS.stage },
          ]}
          sort={sort}
          onSort={onSort}
          minWidth={860}
        >
          {events.data.events.map((event: EventSummary) => (
            <tr key={event._id} className="border-t border-portal-line align-top">
              <td className="w-[72px] p-3">
                <EventDate event={event} />
              </td>
              {/* Capped so the contract·client label doesn't stretch and push
                  the participant count to the far right of the table. */}
              <td className="w-[34%] p-3 text-portal-small">
                <Link
                  to={`/events/${event._id}`}
                  className="text-portal-small font-bold text-brand hover:underline"
                >
                  {[event.contractNumber, event.accountName].filter(Boolean).join(' · ') ||
                    event.programDisplayName ||
                    'Untitled event'}
                </Link>
                <span className="block text-portal-micro text-portal-muted">
                  {event.programDisplayName || event.programName || ''}
                  {(event.familySize ?? 1) > 1 && event.contractNumber ? (
                    <Link
                      to={`/events?family=${encodeURIComponent(event.contractNumber.split(/[.-]/)[0] ?? '')}&state=all`}
                      title={`${event.familySize} events under this contract number`}
                      className="ml-2 rounded-full bg-brand-tint px-1.5 py-px font-semibold text-brand hover:underline"
                    >
                      +{(event.familySize ?? 1) - 1}
                    </Link>
                  ) : null}
                </span>
              </td>
              <td className="w-[110px] p-3 text-portal-small whitespace-nowrap">
                {event.participants ?? '—'}
              </td>
              <td className="p-3 text-portal-small">
                {event.location && event.startVenue?.city ? (
                  <Link
                    to={`/places/events?city=${encodeURIComponent(event.startVenue.city)}${event.startVenue.state ? `&usState=${encodeURIComponent(event.startVenue.state)}` : ''}`}
                    className="text-brand hover:underline"
                  >
                    {event.location}
                  </Link>
                ) : (
                  event.location || '—'
                )}
              </td>
              <td className="p-3 text-portal-small">
                {event.leadFacilitatorId && event.leadFacilitatorName ? (
                  <Link
                    to={`/people/${event.leadFacilitatorId}`}
                    className="text-portal-small text-brand hover:underline"
                  >
                    {event.leadFacilitatorName}
                  </Link>
                ) : (
                  <span className="text-portal-small">{event.leadFacilitatorName || '—'}</span>
                )}
              </td>
              <td className="p-3 text-portal-small">
                {event.stageName ? (
                  <Pill tone={eventStageTone(event.stageName)}>{event.stageName}</Pill>
                ) : null}
                {event.awaitingContract === true ? (
                  <Pill tone="warning">Awaiting contract</Pill>
                ) : null}
              </td>
            </tr>
          ))}
        </DataTable></div>
      )}
      {events.data !== undefined && events.data.totalPages > 1 ? (
        <Pagination guide="events-pagination"
          page={page}
          totalPages={events.data.totalPages}
          onChange={setPage}
          summary={`${(page - 1) * events.data.limit + 1}–${(page - 1) * events.data.limit + events.data.events.length} of ${events.data.total}`}
        />
      ) : null}
    </section>
  );
}
